/**
 * @file     useLessonCompletionReady.ts
 * @readme   ./README.md
 * @purpose  Tracks whether a lesson has been sufficiently engaged with to unlock the Mark Complete button. Combines content-consumed and quiz-answered signals (correctness not required).
 */
'use client';

import { useCallback, useEffect, useState } from 'react';

interface UseLessonCompletionReadyArgs {
  lessonId: string;
  hasQuiz: boolean;
}

interface UseLessonCompletionReadyResult {
  contentConsumed: boolean;
  quizPassed: boolean;
  canComplete: boolean;
  markContentConsumed: () => void;
  markQuizPassed: () => void;
}

export function useLessonCompletionReady({
  lessonId,
  hasQuiz,
}: UseLessonCompletionReadyArgs): UseLessonCompletionReadyResult {
  const [contentConsumed, setContentConsumed] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);

  useEffect(() => {
    setContentConsumed(false);
    setQuizPassed(false);
  }, [lessonId]);

  const markContentConsumed = useCallback(() => setContentConsumed(true), []);
  const markQuizPassed = useCallback(() => setQuizPassed(true), []);

  const canComplete = contentConsumed && (quizPassed || !hasQuiz);

  return {
    contentConsumed,
    quizPassed,
    canComplete,
    markContentConsumed,
    markQuizPassed,
  };
}
